/* eslint-disable no-restricted-syntax */
import { mapActions, mapGetters, mapState } from 'vuex';
import loaderMixin from '@/mixins/loader';
import disqusMixin from '@/mixins/disqus';
import cmsContentMixin from '@/mixins/cmsContent';
import seoMetaMixin from '@/mixins/seoMeta';
import scrollTo from '@/utils/scrollTo';
import { getUniqueViewGUID, pushPreviewUniqueView } from '@/helpers/dataLayer';
import buildCanonicalUrl from '@/utils/canonical-url';
import { buildJsonLdTag, jsonLdSanitizerOverride, buildNewsArticleSchema } from '@/utils/json-ld';

const STORY_JSON_LD_VMID = 'jsonld-story';

export default {
  mixins: [
    cmsContentMixin,
    disqusMixin,
    loaderMixin,
    seoMetaMixin,
  ],
  props: {
    storyId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      storyContent: '',
      refreshIndex: 0,
      previewUniqueViewGUID: '',
    };
  },
  metaInfo() {
    if (this.currentStoryDetail !== null) {
      const canonicalUrl = buildCanonicalUrl(this.$router, 'story-details', {
        storyId: this.storyId,
        storyUrl: this.currentStoryDetail.storyUrl,
      });
      const jsonLdSchema = buildNewsArticleSchema({
        origin: window.location.origin,
        canonicalUrl,
        story: this.currentStoryDetail,
        description: this.storySeoDescription,
      });
      return {
        ...this.buildSeoMeta({
          title: this.currentStoryDetail.seoTitle,
          description: this.storySeoDescription,
          image: this.currentStoryDetail.imageName,
          type: 'article',
          url: canonicalUrl,
          article: {
            publishedTime: this.currentStoryDetail.publishedDate,
            modifiedTime: this.currentStoryDetail.modifiedDate,
          },
        }),
        script: [buildJsonLdTag(jsonLdSchema, STORY_JSON_LD_VMID)],
        __dangerouslyDisableSanitizersByTagID: jsonLdSanitizerOverride(STORY_JSON_LD_VMID),
      };
    }
    return {};
  },
  computed: {
    ...mapState('browse', ['currentStoryDetail']),
    ...mapGetters('app', ['isLayoutMobile']),
    showComments() {
      if (this.currentStoryDetail === null) {
        return false;
      }
      return typeof this.currentStoryDetail.disqus_Visible === 'undefined' || this.currentStoryDetail.disqus_Visible;
    },
    storySeoDescription() {
      const story = this.currentStoryDetail;
      if (!story) return '';
      if (story.seoDescription) return story.seoDescription;
      const plainText = (story.bodyHTML || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      return plainText.slice(0, 160);
    },
  },
  watch: {
    currentStoryDetail: {
      deep: true,
      handler(newVal) {
        if (newVal) {
          this.refreshIndex += 1;
          this.storyContent = `${newVal.bodyCSS || ''}${newVal.bodyHTML || ''}`;

          this.previewUniqueViewGUID = getUniqueViewGUID();
          const sectionId = (newVal.cmsTrackingId && newVal.cmsTrackingId.split('-')[1]) || '';
          pushPreviewUniqueView({
            storyId: newVal.storyId,
            storyVersionId: newVal.versionId || 0,
            sectionId,
            sectionType: this.isLayoutMobile ? 'Mobile' : 'Desktop',
            previewUniqueViewGUID: this.previewUniqueViewGUID,
          });

          this.$nextTick(() => {
            this.installDynamicControls({
              data: {
                ...newVal,
              },
            });
          });
        }
      },
    },
  },
  methods: {
    ...mapActions('browse', ['getStoryDetail', 'clearCurrentStoryDetail']),
    getCanonicalStoryUrl() {
      if (!this.currentStoryDetail) {
        return '';
      }
      return (this.currentStoryDetail.storyUrl || '').trim();
    },
    syncCanonicalStoryUrl() {
      const canonicalStoryUrl = this.getCanonicalStoryUrl();
      const currentStoryUrl = ((this.$route.params || {}).storyUrl || '').trim();

      if (currentStoryUrl === canonicalStoryUrl) {
        return;
      }

      const params = {
        storyId: this.storyId,
      };
      if (canonicalStoryUrl.length > 0) {
        params.storyUrl = canonicalStoryUrl;
      }

      this.$router.replace({
        name: 'story-details',
        params,
        query: this.$route.query,
        hash: this.$route.hash,
      });
    },
  },
  mounted() {
    this.loaderAdd(document.body);
    this.getStoryDetail({ index: this.storyId })
      .then(() => {
        this.syncCanonicalStoryUrl();
        this.loaderRemove(document.body);

        if (!window.location.hash) return;

        this.$nextTick(() => {
          const elementId = window.location.hash.replace('#', '');
          const element = window.document.getElementById(elementId);
          if (element !== null) {
            scrollTo(element);
          }
        });
      })
      .catch(() => this.loaderRemove(document.body));
  },
  beforeDestroy() {
    this.clearCurrentStoryDetail();
  },
};



// WEBPACK FOOTER //
// ./src/components/pages/page-story-detail/page-story-detail.js